import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, writeOrgFile } from '../../storage/FileSystemAdapter';
import AssetList from './AssetList';
import AssetForm from './AssetForm';
import ImportWizard from '../shared/ImportWizard';
import ConfirmDialog from '../shared/ConfirmDialog';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_SAVE_AND_LEAVE, TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, ERROR_BG, ERROR_TEXT, ERROR_BORDER, FONT_PAGE_TITLE, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import { CRITICALITY_LEVELS } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:       'Inventario de activos',
    backToList:  '← Volver a la lista',
    clauseRef:   'Control A.5.9 · ISO/IEC 27001:2022',
    loadError:   'Error al leer los datos. Comprueba el acceso a la carpeta.',
    retry:       'Reintentar',
    importCsv:   'Importar CSV',
    importTitle: 'Importar activos',
    newAsset:    '+ Nuevo activo',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     '¿Salir sin guardar los cambios?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    deleteBtn:          'Eliminar',
  },
  en: {
    title:       'Asset inventory',
    backToList:  '← Back to list',
    clauseRef:   'Control A.5.9 · ISO/IEC 27001:2022',
    loadError:   'Failed to read data. Check folder access.',
    retry:       'Retry',
    importCsv:   'Import CSV',
    importTitle: 'Import assets',
    newAsset:    '+ New asset',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'Leave without saving changes?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    deleteBtn:          'Delete',
  },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function AssetsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [assets,    setAssets]    = useState(null);   // null = loading
  const [formMode,  setFormMode]  = useState(null);   // null | 'create' | 'edit'
  const [editAsset, setEditAsset] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next successful form save reports back via onSaveResult instead
  // of running the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef           = useRef(false);
  const lastProcessedAppTickRef     = useRef(0);

  useScrollToTopOnFormOpen(formMode !== null);

  const loadAssets = () => {
    setLoadError(false);
    listOrgCollection(org.id, 'assets')
      .then((items) => {
        const sorted = [...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''));
        setAssets(sorted);
      })
      .catch(() => { setAssets([]); setLoadError(true); });
  };

  useEffect(() => {
    loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditAsset(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadAssets();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadAssets();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => { setFormMode(null); setEditAsset(null); setDirty(false); };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Import view ──────────────────────────────────────────────────────────────
  if (showImport && !formMode) {
    const importClassifications = (Array.isArray(org.documentClassification) && org.documentClassification.length > 0) ? org.documentClassification : ['Public','Internal','Confidential','Restricted'];
    const importDefaultClass = importClassifications.includes('Internal')
      ? 'Internal'
      : (importClassifications[0] || 'Internal');
    return (
      <div>
        <ImportWizard
          lang={lang}
          dedupeKey="name"
          onClose={() => setShowImport(false)}
          onImport={async (rows) => {
            const existing = await listOrgCollection(org.id, 'assets').catch(() => []);
            const year = new Date().getFullYear();
            const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const now = new Date().toISOString();
            const todayDate = now.slice(0, 10);

            const dayPrefix = `asset-${day}-`;
            let maxFileSeq = 0;
            existing.forEach(a => {
              if (a.id && a.id.startsWith(dayPrefix)) {
                const n = parseInt(a.id.slice(dayPrefix.length), 10);
                if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
              }
            });

            const assetPrefix = (org.refPrefixes && org.refPrefixes.asset) || 'ast';
            const yearPrefix = `${assetPrefix}-${year}-`;
            let maxRefSeq = 0;
            existing.forEach(a => {
              if (a.ref && a.ref.startsWith(yearPrefix)) {
                const n = parseInt(a.ref.slice(yearPrefix.length), 10);
                if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
              }
            });

            const toWrite = [];
            for (const row of rows) {
              // Skip if name already exists (including within this batch)
              const dup = existing.find(a => a.name && a.name.toLowerCase() === (row.name || '').toLowerCase());
              if (dup) continue;

              const fileId = `${dayPrefix}${String(++maxFileSeq).padStart(3, '0')}`;
              const ref = `${yearPrefix}${String(++maxRefSeq).padStart(3, '0')}`;

              const assetObj = {
                id: fileId, orgId: org.id, ref,
                name: row.name || '', type: row.type || 'Information',
                description: row.description || '',
                owner: row.owner || '', custodian: row.custodian || null,
                criticality: row.criticality || 'Medium',
                classification: row.classification || importDefaultClass,
                personalDataContained: !!row.personalDataContained,
                location: row.location || '',
                linkedSupplierIds: [],
                status: 'Active', reviewDate: todayDate,
                createdAt: now, updatedAt: now,
              };
              toWrite.push(assetObj);
              // Add to existing to prevent duplicates within same batch
              existing.push(assetObj);
            }

            // Write all assets concurrently. The File System Access API
            // invalidates cached directory handles once a write modifies a
            // directory, which breaks a sequential for-await loop. Starting
            // every write from the same initial directory state avoids this.
            try {
              await Promise.all(
                toWrite.map(a => writeOrgFile(org.id, `assets/${a.id}.json`, a))
              );
            } catch (err) {
              loadAssets();
              const msg = lang === 'es'
                ? 'No se pudieron guardar todos los activos. Inténtalo de nuevo.'
                : 'Could not save all assets. Please try again.';
              throw new Error(msg);
            }
            loadAssets();
            return toWrite.length;
          }}
          columns={[
            { key: 'name',               label: 'Name',               required: true,  type: 'string' },
            { key: 'type',               label: 'Type',               required: true,  type: 'string',  enum: ['Information','Software','Hardware','Service','People','Premises','Other'], caseInsensitive: true },
            { key: 'description',        label: 'Description',        required: false, type: 'string',  default: '' },
            { key: 'owner',              label: 'Owner',              required: true,  type: 'string' },
            { key: 'custodian',          label: 'Custodian',          required: false, type: 'string',  default: null },
            { key: 'criticality',        label: 'Criticality',        required: true,  type: 'string',  enum: CRITICALITY_LEVELS, caseInsensitive: true },
            { key: 'classification',     label: 'Classification',     required: false, type: 'string',  enum: importClassifications, default: importDefaultClass, caseInsensitive: true },
            { key: 'personalDataContained',label:'Personal Data Contained',required:false,type:'boolean', default: false },
            { key: 'location',           label: 'Location',           required: false, type: 'string',  default: '' },
          ]}
          templateFilename="import-assets.csv"
          title={{ es: t.importTitle, en: 'Import assets' }}
        />
      </div>
    );
  }

  // ── Form view ────────────────────────────────────────────────────────────────
  if (formMode) {
    return (
      <div>
        <button
          onClick={handleFormCancel}
          style={{
            ...FONT_LABEL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, letterSpacing: '0.03em', padding: 0,
            marginBottom: '0.75rem', display: 'block',
          }}
        >
          {t.backToList}
        </button>
        <AssetForm
          asset={formMode === 'create' ? null : editAsset}
          mode={formMode}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>
            {t.title}
          </h1>
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.15rem 0 0', letterSpacing: '0.03em' }}>
            {t.clauseRef}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <button
            onClick={() => setShowImport(true)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            }}
          >
            {t.importCsv}
          </button>
          <button
            onClick={() => { setFormMode('create'); setDirty(false); }}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            }}
          >
            {t.newAsset}
          </button>
        </div>
      </div>

      {/* Load error banner */}
      {loadError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
          color: ERROR_TEXT,
        }}>
          <span>{t.loadError}</span>
          <button
            onClick={loadAssets}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE,
              ...FONT_SMALL,
              borderRadius: 6,
              padding: '0.25rem 0.6rem', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* List */}
      <AssetList
        assets={assets}
        loading={assets === null}
        org={org}
        lang={lang}
        onNew={() => { setFormMode('create'); setDirty(false); }}
        onEdit={(a) => { setEditAsset(a); setFormMode('edit'); setDirty(false); }}
        onView={(a) => { setEditAsset(a); setFormMode('view'); setDirty(false); }}
        onNavigate={onNavigate}
        onAssetsChanged={loadAssets}
      />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
